import { LocoSyncClient } from '@teamworksdev/loco-sync-client';
import { createLocoSyncIdbAdapter } from '@teamworksdev/loco-sync-idb';
import { createLocoSyncReact } from '@teamworksdev/loco-sync-react';

import { MS, config } from '@strength/loco-sync';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { LoadingPage } from '../app/pages/loading';
import { useAuth, useOrgAuthDataOrThrow } from '../auth';
import { clientDbName } from './db-hash';
import { createNetworkAdapter } from './network/create-adapter';

const { Provider, ...hooks } = createLocoSyncReact(config);

export const { useMutation, useQuery, useQueryOne, useIsHydrated, useClient } =
  hooks;

export function LocoSyncProvider(props: PropsWithChildren<{}>) {
  const auth = useOrgAuthDataOrThrow();
  const { setAuth } = useAuth();

  const hash = clientDbName(auth);
  const syncClientRef = useRef<{
    client: LocoSyncClient<MS>;
    hash: string;
  }>();

  // Cleanup old databases when hash changes
  useEffect(() => {
    // This is technically a promise, but we don't need to wait on it
    deleteOldDbs(auth.profileId, auth.orgId, hash);
  }, [hash, auth.profileId, auth.orgId]);

  if (!syncClientRef.current || syncClientRef.current.hash !== hash) {
    const storage = createLocoSyncIdbAdapter<MS>(hash, config, {
      onBlocking: () => window.location.reload(),
    });
    const network = createNetworkAdapter(() => setAuth(null));
    const client = new LocoSyncClient({
      storage,
      network,
      config,
    });

    syncClientRef.current = { client, hash };
  }
  const syncClient = syncClientRef.current.client;

  return (
    <Provider client={syncClient} notHydratedFallback={<LoadingPage />}>
      {props.children}
    </Provider>
  );
}

async function deleteOldDbs(profileId: string, orgId: string, hash: string) {
  if (!indexedDB.databases) {
    console.warn('indexedDB.databases() not supported, skipping cleanup');
    return;
  }

  let dbName;
  try {
    const databases = await indexedDB.databases();
    // Only delete dbs of the logged in user
    const userPrefix = `${profileId}|${orgId}|`;

    for (const db of databases) {
      dbName = db.name;
      if (!dbName) continue;

      if (dbName.startsWith(userPrefix)) {
        // Don't delete the current db
        if (dbName !== hash) {
          console.log(`Deleting old IndexedDB: ${dbName}`);
          indexedDB.deleteDatabase(dbName);
        }
      }
    }
  } catch (error) {
    console.error(
      `Error cleaning up IDB: ${dbName ?? 'could not get database list'}`,
      error,
    );
  }
}
